import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useNavigationType} from "react-router-dom";
import {path} from "src/router/path";
import {ARTIST, getArtistFromPath} from "src/utils/artist.utils";
import {
  BackButton,
  ArtistBackground,
  Button,
  CharacterWithBubble,
  LanguageSelector,
  Modal,
} from "../components";
import {ConnectionState} from "../hooks/socket/socket.type";
import {useWaitingQueue} from "../services/waitingQueue";
import networkDisconnectedIcon from "../assets/images/common/network_disconnected_icon.png";
import "../styles/WaitingQueue.scss";

const WaitingQueue = () => {
  const navigate = useNavigate();
  const {t} = useTranslation("common");
  const navigationType = useNavigationType();

  const {state} = useLocation();
  const location = useLocation();
  const artist = getArtistFromPath(location.pathname);

  const handleEnterCodeAllowed = useCallback(() => {
    navigate(path.code(artist), {replace: true});
  }, [navigate, artist]);

  const handleForegroundSessionNone = useCallback(() => {
    navigate(path.waiting(artist), {replace: true});
  }, [navigate, artist]);

  const handleServiceUnavailable = useCallback(() => {
    navigate(path.error(artist), {replace: true});
  }, [navigate, artist]);

  // POP(새로고침/뒤로가기) 시 stale한 state를 초기값으로 사용하지 않음
  const initialQueueInfo =
    navigationType !== "POP" ? state?.queueInfo : undefined;

  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const hasConnectedRef = useRef(false);

  const handleNetworkModalButton = useCallback(() => {
    navigate(path.waiting(artist), {replace: true});
  }, [navigate, artist]);

  const {connectionState, queueStatusData, registerQueue, isError} =
    useWaitingQueue({
      artist,
      registeredQueueInfo: initialQueueInfo,
      onEnterCodeAllowed: handleEnterCodeAllowed,
      onForegroundSessionNone: handleForegroundSessionNone,
      onServiceUnavailable: handleServiceUnavailable,
    });

  useEffect(() => {
    if (navigationType === "POP") {
      registerQueue();
    }
  }, [navigationType]);

  useEffect(() => {
    if (isError) {
      navigate(path.error(artist), {replace: true});
    }
  }, [isError]);

  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      hasConnectedRef.current = true;
      return;
    }
    if (
      connectionState === ConnectionState.DISCONNECTED &&
      hasConnectedRef.current
    ) {
      setShowNetworkModal(true);
    }
  }, [connectionState]);

  const handleBack = () => {
    navigate(path.waiting(artist), {replace: true});
  };

  return (
    <div className="waiting-queue-container">
      {showNetworkModal && (
        <Modal>
          <div className="network-disconnected-modal-wrapper">
            <div className="network-disconnected-modal-content">
              <div className="network-disconnected-modal-icon">
                <img src={networkDisconnectedIcon} alt="" />
              </div>
              <div className="network-disconnected-modal-information">
                <p className="network-disconnected-modal-title">
                  {t("queue.networkDisconnectedModal.title")}
                </p>
                <p className="network-disconnected-modal-description">
                  {t("queue.networkDisconnectedModal.description")}
                </p>
              </div>
            </div>
            <Button
              artist={artist}
              label={t("queue.networkDisconnectedModal.button")}
              onClick={handleNetworkModalButton}
            />
          </div>
        </Modal>
      )}
      <ArtistBackground artist={artist} />
      <div className="waiting-queue-header">
        <BackButton artist={artist} onClick={handleBack} iconType="close" />
        <LanguageSelector artist={artist} />
      </div>
      <div className="waiting-queue-content">
        <div className="waiting-queue-label">
          <p className="waiting-queue-label-title">{t("queue.title")}</p>
          <p className="waiting-queue-label-description">
            {t("queue.description")}
          </p>
        </div>
        <div className="waiting-queue-body">
          <CharacterWithBubble
            content={
              <>
                <p className={`waiting-queue-sequence-label ${artist}`}>
                  {t("queue.sequenceLabel")}
                </p>
                <p className={`waiting-queue-sequence-value ${artist}`}>
                  {t("queue.sequence", {
                    position: queueStatusData?.position ?? 0,
                  })}
                </p>
              </>
            }
            artist={artist}
            character={artist === ARTIST.aespa ? "karangi" : "rizuko"}
          />
          <p className="waiting-queue-notice">{t("queue.notice")}</p>
        </div>
      </div>
      <div />
    </div>
  );
};

export default WaitingQueue;
